/**
 * Circular breadcrumb buffer. Records the last ~30 user/app events so
 * that when an error fires we can ship the sequence of actions that
 * led up to it, not just the crash itself.
 *
 * Kept dependency-free and sync so it can't itself cause failures.
 * Sensitive data (request bodies, form values, auth headers) is never
 * captured — only structural metadata.
 */

const MAX_BREADCRUMBS = 30;
const buffer = [];

export const BREADCRUMB_TYPES = {
  NAV: "nav", // route change
  API: "api", // http request / response
  CLICK: "click", // user action on a tagged element
  ACTION: "action", // manual: important domain operation (trip created, flight saved)
  CONSOLE: "console", // console.error / console.warn
  INFO: "info", // misc
};

function hhmmss() {
  const d = new Date();
  return (
    String(d.getHours()).padStart(2, "0") +
    ":" +
    String(d.getMinutes()).padStart(2, "0") +
    ":" +
    String(d.getSeconds()).padStart(2, "0")
  );
}

export function addBreadcrumb(type, data, extra = null) {
  try {
    buffer.push({
      t: hhmmss(),
      type,
      data: String(data).slice(0, 300),
      ...(extra ? { extra } : {}),
    });
    if (buffer.length > MAX_BREADCRUMBS) buffer.shift();
  } catch {
    // never throw from breadcrumb capture
  }
}

export function getBreadcrumbs() {
  return buffer.slice();
}

export function clearBreadcrumbs() {
  buffer.length = 0;
}

// ── Auto-capture: console.error / console.warn ──────────────────────────────
// Wrap the globals so any existing call site (including ours) leaves a trail.
// We preserve the original behaviour so developers still see everything in
// devtools.
const _origError = console.error.bind(console);
const _origWarn = console.warn.bind(console);

console.error = (...args) => {
  try {
    const msg = args
      .map((a) => (a instanceof Error ? `${a.name}: ${a.message}` : String(a)))
      .join(" ");
    addBreadcrumb(BREADCRUMB_TYPES.CONSOLE, `error: ${msg}`);
  } catch {}
  _origError(...args);
};

console.warn = (...args) => {
  try {
    const msg = args.map(String).join(" ");
    addBreadcrumb(BREADCRUMB_TYPES.CONSOLE, `warn: ${msg}`);
  } catch {}
  _origWarn(...args);
};

// ── Auto-capture: route changes ─────────────────────────────────────────────
// Intercept pushState/replaceState so any router library we use leaves a
// trail. Works with React Router, hash router, etc.
let _lastPath = typeof window !== "undefined" ? window.location.pathname : "";

function captureNav(newPath) {
  if (!newPath || newPath === _lastPath) return;
  addBreadcrumb(BREADCRUMB_TYPES.NAV, `${_lastPath} → ${newPath}`);
  _lastPath = newPath;
}

if (typeof window !== "undefined") {
  const _origPush = window.history.pushState;
  const _origReplace = window.history.replaceState;
  window.history.pushState = function (...args) {
    const r = _origPush.apply(this, args);
    captureNav(window.location.pathname);
    return r;
  };
  window.history.replaceState = function (...args) {
    const r = _origReplace.apply(this, args);
    captureNav(window.location.pathname);
    return r;
  };
  window.addEventListener("popstate", () => captureNav(window.location.pathname));
}

// ── Auto-capture: significant clicks ────────────────────────────────────────
// To avoid recording every mouse click, we only record when the clicked
// element (or ancestor) has a `data-breadcrumb` attribute OR is a button
// with readable text. Buttons are the main action affordance in this app.
if (typeof window !== "undefined") {
  window.addEventListener(
    "click",
    (e) => {
      try {
        let el = e.target;
        let depth = 0;
        while (el && depth < 5) {
          if (el.dataset?.breadcrumb) {
            addBreadcrumb(BREADCRUMB_TYPES.CLICK, el.dataset.breadcrumb);
            return;
          }
          if (el.tagName === "BUTTON") {
            const text = (el.getAttribute("aria-label") || el.innerText || "").trim().slice(0, 80);
            if (text) addBreadcrumb(BREADCRUMB_TYPES.CLICK, `button: ${text}`);
            return;
          }
          if (el.tagName === "A" && el.href) {
            addBreadcrumb(BREADCRUMB_TYPES.CLICK, `link: ${el.getAttribute("href")}`);
            return;
          }
          el = el.parentElement;
          depth += 1;
        }
      } catch {}
    },
    true
  );
}
